import React, { useState, useEffect, useCallback } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import api from '../config/axios';

Chart.register(...registerables);

interface SalesData {
  seller_name: string;
  total_amount: number;
  total_sales: number;
}

interface TeamData {
  team_name: string;
  team_id: string;
  total_amount: number;
  total_sales: number;
}

interface SellerByTeamData {
  team_name: string;
  team_id: string;
  seller_name: string;
  seller_email: string;
  total_amount: number;
  total_sales: number;
}

interface RentaAreaData {
  area: string;
  monto_total: number;
  total_cargo_fijo: number;
  vendedores_count: number;
}

interface RentaSellerData {
  seller_email: string;
  seller_name: string;
  monto_total: number;
  total_cargo_fijo: number;
}

interface AvailableMonth {
  value: string; // "2025-11"
  label: string; // "Noviembre-2025"
  year: number;
  month: number;
}

interface StatusData {
  estado_agrupado: string;
  monto_total: number;
  total_cargo_fijo: number;
  cantidad_ventas: number;
}

const SalesCharts: React.FC = () => {
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [teamData, setTeamData] = useState<TeamData[]>([]);
  const [sellerByTeamData, setSellerByTeamData] = useState<SellerByTeamData[]>([]);
  const [monthlySalesData, setMonthlySalesData] = useState<any[]>([]);
  const [rentaAreaData, setRentaAreaData] = useState<RentaAreaData[]>([]);
  const [rentaSellerData, setRentaSellerData] = useState<RentaSellerData[]>([]);
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState<string>(''); // "2025-11"
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState<'all' | 'teams' | 'comparison' | 'renta' | 'status'>('all');

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  // const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i); // No usado actualmente

  const loadSalesData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar datos de ventas por vendedor
      const salesResponse = await api.get('/sales', {
        params: {
          month: selectedMonth,
          year: selectedYear,
          groupBy: 'seller'
        }
      });

      // Cargar datos de ventas por equipo
      const teamResponse = await api.get('/sales', {
        params: {
          month: selectedMonth,
          year: selectedYear,
          groupBy: 'team'
        }
      });

      // Cargar datos de vendedores por equipo
      const sellerByTeamResponse = await api.get('/sales', {
        params: {
          month: selectedMonth,
          year: selectedYear,
          groupBy: 'seller_by_team'
        }
      });

      // Cargar datos mensuales para gráfico de línea
      const monthlyResponse = await api.get('/sales', {
        params: {
          month: selectedMonth,
          year: selectedYear,
          groupBy: 'month'
        }
      });

      setSalesData(salesResponse.data || []);
      setTeamData(teamResponse.data || []);
      setSellerByTeamData(sellerByTeamResponse.data || []);
      setMonthlySalesData(monthlyResponse.data || []);

      console.log('Sales data:', salesResponse.data);
      console.log('Team data:', teamResponse.data);
      console.log('Seller by team data:', sellerByTeamResponse.data);
    } catch (error) {
      console.error('Error loading sales data for charts:', error);
      setSalesData([]);
      setTeamData([]);
      setSellerByTeamData([]);
      setMonthlySalesData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  const loadRentaData = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedArea) {
        // Cargar datos por vendedor del área seleccionada
        const response = await api.get('/reports/renta', {
          params: {
            month: selectedMonth,
            year: selectedYear,
            segment: selectedArea
          }
        });
        setRentaSellerData(response.data || []);
        setRentaAreaData([]);
      } else {
        // Cargar datos agregados por área
        const response = await api.get('/reports/renta', {
          params: {
            month: selectedMonth,
            year: selectedYear
          }
        });
        setRentaAreaData(response.data || []);
        setRentaSellerData([]);
      }
    } catch (error) {
      console.error('Error loading renta data:', error);
      setRentaAreaData([]);
      setRentaSellerData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedArea]);

  const loadStatusData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/status', {
        params: {
          month: selectedMonth,
          year: selectedYear
        }
      });
      setStatusData(response.data || []);
    } catch (error) {
      console.error('Error loading status data:', error);
      setStatusData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Cargar meses disponibles al montar el componente
  useEffect(() => {
    const loadAvailableMonths = async () => {
      try {
        const response = await api.get('/reports/available-months');
        const months = response.data || [];
        setAvailableMonths(months);
        // Seleccionar el primer mes (más reciente) por defecto
        if (months.length > 0 && !selectedMonthYear) {
          setSelectedMonthYear(months[0].value);
          const [year, month] = months[0].value.split('-').map(Number);
          setSelectedYear(year);
          setSelectedMonth(month);
        }
      } catch (error) {
        console.error('Error loading available months:', error);
      }
    };
    loadAvailableMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Actualizar mes y año cuando cambia selectedMonthYear
  useEffect(() => {
    if (selectedMonthYear) {
      const [year, month] = selectedMonthYear.split('-').map(Number);
      setSelectedYear(year);
      setSelectedMonth(month);
    }
  }, [selectedMonthYear]);

  useEffect(() => {
    loadSalesData();
  }, [loadSalesData]);

  useEffect(() => {
    if (activeTab === 'renta') {
      loadRentaData();
    } else if (activeTab === 'status') {
      loadStatusData();
    }
  }, [activeTab, loadRentaData, loadStatusData, selectedMonthYear]);

  // Agrupar vendedores por equipo
  const groupedByTeam = sellerByTeamData.reduce((acc: any, item) => {
    if (!acc[item.team_name]) {
      acc[item.team_name] = [];
    }
    acc[item.team_name].push(item);
    return acc;
  }, {});

  // Datos para gráfico de barras por vendedor
  const barChartData = {
    labels: salesData.map(s => s.seller_name),
    datasets: [
      {
        label: 'Monto Total (S/)',
        data: salesData.map(s => s.total_amount),
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: 'Número de Ventas',
        data: salesData.map(s => s.total_sales),
        backgroundColor: 'rgba(54, 162, 235, 0.3)',
        borderColor: 'rgba(54, 162, 235, 0.8)',
        borderWidth: 1,
        yAxisID: 'y1',
      }
    ]
  };

  // Datos para gráfico de equipos
  const teamBarChartData = {
    labels: teamData.map(t => t.team_name),
    datasets: [
      {
        label: 'Monto Total (S/)',
        data: teamData.map(t => t.total_amount),
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: 'Número de Ventas',
        data: teamData.map(t => t.total_sales),
        backgroundColor: 'rgba(75, 192, 192, 0.3)',
        borderColor: 'rgba(75, 192, 192, 0.8)',
        borderWidth: 1,
        yAxisID: 'y1',
      }
    ]
  };

  // Datos para gráfico de dona por equipos
  const teamDoughnutData = {
    labels: teamData.map(t => t.team_name),
    datasets: [{
      data: teamData.map(t => t.total_amount),
      backgroundColor: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Datos para gráfico de dona por vendedores (fallback si no hay datos de equipos)
  const sellerDoughnutData = {
    labels: salesData.map(s => s.seller_name),
    datasets: [{
      data: salesData.map(s => s.total_amount),
      backgroundColor: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Usar datos de equipos si están disponibles, sino usar datos de vendedores
  const doughnutData = teamData.length > 0 ? teamDoughnutData : sellerDoughnutData;
  
  // Debug: mostrar qué datos tenemos
  console.log('Doughnut data labels:', doughnutData.labels);
  console.log('Doughnut data datasets:', doughnutData.datasets);

  // Datos para gráfico de línea mensual
  const lineChartData = {
    labels: monthlySalesData.map((item: any) => {
      const date = new Date(item.date);
      return date.getDate();
    }),
    datasets: [{
      label: 'Ventas Diarias (S/)',
      data: monthlySalesData.map((item: any) => item.amount),
      borderColor: 'rgba(255, 99, 132, 1)',
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      tension: 0.1,
      fill: true
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
            weight: 'normal' as const
          }
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        ticks: {
          callback: function(value: any) {
            return 'S/ ' + value.toLocaleString();
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function(value: any) {
            return value + ' ventas';
          }
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 11,
            weight: 'normal' as const
          }
        }
      }
    }
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
            weight: 'normal' as const
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear' as const,
        ticks: {
          callback: function(value: any) {
            return `Día ${Math.round(value)}`;
          }
        }
      },
      y: {
        ticks: {
          callback: function(value: any) {
            return 'S/ ' + value.toLocaleString();
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando datos del dashboard...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Selectores de fecha */}
      <div className="row mb-4">
        <div className="col-md-6">
          {/* Selector único con meses disponibles para todas las pestañas */}
          <div className="row g-3">
            <div className="col-md-12">
              <label className="form-label">Período</label>
              <select 
                className="form-select" 
                value={selectedMonthYear} 
                onChange={(e) => setSelectedMonthYear(e.target.value)}
                disabled={availableMonths.length === 0}
              >
                {availableMonths.length === 0 ? (
                  <option>Cargando meses disponibles...</option>
                ) : (
                  availableMonths.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Pestañas */}
      <ul className="nav nav-tabs mb-4" id="dashboardTabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button 
            className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            type="button"
          >
            <i className="fas fa-users me-2"></i>
            Todos los Vendedores
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button 
            className={`nav-link ${activeTab === 'teams' ? 'active' : ''}`}
            onClick={() => setActiveTab('teams')}
            type="button"
          >
            <i className="fas fa-layer-group me-2"></i>
            Por Equipos
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button 
            className={`nav-link ${activeTab === 'comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('comparison')}
            type="button"
          >
            <i className="fas fa-chart-bar me-2"></i>
            Comparación de Equipos
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button 
            className={`nav-link ${activeTab === 'renta' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('renta');
              setSelectedArea(null);
            }}
            type="button"
          >
            <i className="fas fa-money-bill-wave me-2"></i>
            Renta
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button 
            className={`nav-link ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
            type="button"
          >
            <i className="fas fa-tasks me-2"></i>
            Por Estado
          </button>
        </li>
      </ul>

      {/* Contenido de las pestañas */}
      <div className="tab-content">
        {/* Pestaña: Todos los Vendedores */}
        {activeTab === 'all' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    Ventas por Vendedor - {months[selectedMonth - 1]?.label} {selectedYear}
                  </h5>
                  <small className="text-muted">
                    Barras azules: Monto Total | Barras transparentes: Número de Ventas
                  </small>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Bar data={barChartData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Distribución de Ventas</h5>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pestaña: Por Equipos */}
        {activeTab === 'teams' && (
          <div className="row">
            {Object.keys(groupedByTeam).map(teamName => (
              <div key={teamName} className="col-lg-6 mb-4">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-layer-group me-2"></i>
                      {teamName}
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-6">
                        <div className="text-center">
                          <h4 className="text-primary mb-0">
                            S/ {groupedByTeam[teamName].reduce((sum: number, seller: any) => sum + seller.total_amount, 0).toLocaleString()}
                          </h4>
                          <small className="text-muted">Total Vendido</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center">
                          <h4 className="text-success mb-0">
                            {groupedByTeam[teamName].reduce((sum: number, seller: any) => sum + seller.total_sales, 0)}
                          </h4>
                          <small className="text-muted">Total Ventas</small>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ height: '300px' }}>
                      <Bar 
                        data={{
                          labels: groupedByTeam[teamName].map((s: any) => s.seller_name),
                          datasets: [
                            {
                              label: 'Monto (S/)',
                              data: groupedByTeam[teamName].map((s: any) => s.total_amount),
                              backgroundColor: 'rgba(75, 192, 192, 0.8)',
                              borderColor: 'rgba(75, 192, 192, 1)',
                              borderWidth: 1,
                            },
                            {
                              label: 'Ventas',
                              data: groupedByTeam[teamName].map((s: any) => s.total_sales),
                              backgroundColor: 'rgba(75, 192, 192, 0.3)',
                              borderColor: 'rgba(75, 192, 192, 0.8)',
                              borderWidth: 1,
                            }
                          ]
                        }} 
                        options={chartOptions} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pestaña: Comparación de Equipos */}
        {activeTab === 'comparison' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    Comparación de Equipos - {months[selectedMonth - 1]?.label} {selectedYear}
                  </h5>
                  <small className="text-muted">
                    Barras verdes: Monto Total | Barras transparentes: Número de Ventas
                  </small>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Bar data={teamBarChartData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Distribución por Equipos</h5>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tabla de resumen de equipos */}
            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Resumen de Equipos</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Equipo</th>
                          <th>Total Vendido</th>
                          <th>Número de Ventas</th>
                          <th>Promedio por Venta</th>
                          <th>Vendedores Activos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamData.map(team => {
                          const teamSellers = groupedByTeam[team.team_name] || [];
                          const avgPerSale = team.total_sales > 0 ? team.total_amount / team.total_sales : 0;
                          return (
                            <tr key={team.team_id}>
                              <td>
                                <strong>{team.team_name}</strong>
                              </td>
                              <td>
                                <span className="text-success fw-bold">
                                  S/ {team.total_amount.toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {team.total_sales} ventas
                                </span>
                              </td>
                              <td>
                                S/ {avgPerSale.toFixed(2)}
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {teamSellers.length} vendedores
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pestaña: Renta */}
        {activeTab === 'renta' && (
          <div className="row">
            {!selectedArea ? (
              // Vista inicial: mostrar por área
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      Renta por Área - {months[selectedMonth - 1]?.label} {selectedYear}
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Área</th>
                            <th>Monto Total</th>
                            <th>Total Cargo Fijo</th>
                            <th>Vendedores</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rentaAreaData.map((area) => (
                            <tr key={area.area} style={{ cursor: 'pointer' }}>
                              <td>
                                <strong>{area.area}</strong>
                              </td>
                              <td>
                                <span className="text-success fw-bold">
                                  S/ {Number(area.monto_total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td>
                                <span className="text-primary fw-bold">
                                  S/ {Number(area.total_cargo_fijo).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {area.vendedores_count} vendedores
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setSelectedArea(area.area)}
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  Ver Detalle
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Vista detallada: mostrar por vendedores del área seleccionada
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      Renta por Vendedor - Área {selectedArea} - {months[selectedMonth - 1]?.label} {selectedYear}
                    </h5>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedArea(null)}
                    >
                      <i className="fas fa-arrow-left me-1"></i>
                      Volver a Áreas
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Vendedor</th>
                            <th>Email</th>
                            <th>Monto Total</th>
                            <th>Total Cargo Fijo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rentaSellerData.map((seller) => (
                            <tr key={seller.seller_email}>
                              <td>
                                <strong>{seller.seller_name}</strong>
                              </td>
                              <td>
                                <small className="text-muted">{seller.seller_email}</small>
                              </td>
                              <td>
                                <span className="text-success fw-bold">
                                  S/ {Number(seller.monto_total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td>
                                <span className="text-primary fw-bold">
                                  S/ {Number(seller.total_cargo_fijo).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {rentaSellerData.length === 0 && (
                            <tr>
                              <td colSpan={4} className="text-center text-muted">
                                No hay datos disponibles
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {rentaSellerData.length > 0 && (
                          <tfoot>
                            <tr className="table-info">
                              <td colSpan={2}>
                                <strong>TOTAL</strong>
                              </td>
                              <td>
                                <strong>
                                  S/ {rentaSellerData.reduce((sum, s) => sum + Number(s.monto_total), 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </strong>
                              </td>
                              <td>
                                <strong>
                                  S/ {rentaSellerData.reduce((sum, s) => sum + Number(s.total_cargo_fijo), 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </strong>
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pestaña: Por Estado */}
        {activeTab === 'status' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    Ventas por Estado - {months[selectedMonth - 1]?.label} {selectedYear}
                  </h5>
                  <small className="text-muted">
                    Agrupación: INSTALADO (Entregado + Completado) | EN PROCESO (resto de estados)
                  </small>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Bar 
                      data={{
                        labels: statusData.map(s => s.estado_agrupado),
                        datasets: [
                          {
                            label: 'Monto Total (S/)',
                            data: statusData.map(s => Number(s.monto_total)),
                            backgroundColor: 'rgba(75, 192, 192, 0.8)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 1,
                            yAxisID: 'y',
                          },
                          {
                            label: 'Total Cargo Fijo (S/)',
                            data: statusData.map(s => Number(s.total_cargo_fijo)),
                            backgroundColor: 'rgba(54, 162, 235, 0.8)',
                            borderColor: 'rgba(54, 162, 235, 1)',
                            borderWidth: 1,
                            yAxisID: 'y',
                          }
                        ]
                      }} 
                      options={chartOptions} 
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Distribución por Estado</h5>
                </div>
                <div className="card-body">
                  <div style={{ height: '400px' }}>
                    <Doughnut 
                      data={{
                        labels: statusData.map(s => s.estado_agrupado),
                        datasets: [{
                          data: statusData.map(s => Number(s.monto_total)),
                          backgroundColor: [
                            '#4BC0C0', // INSTALADO
                            '#FF6384'  // EN PROCESO
                          ],
                          borderWidth: 2,
                          borderColor: '#fff'
                        }]
                      }} 
                      options={doughnutOptions} 
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tabla de resumen por estado */}
            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Resumen por Estado</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Estado Agrupado</th>
                          <th>Monto Total</th>
                          <th>Total Cargo Fijo</th>
                          <th>Cantidad de Ventas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusData.map((status) => (
                          <tr key={status.estado_agrupado}>
                            <td>
                              <strong>
                                {status.estado_agrupado === 'INSTALADO' ? (
                                  <span className="badge bg-success">{status.estado_agrupado}</span>
                                ) : (
                                  <span className="badge bg-warning text-dark">{status.estado_agrupado}</span>
                                )}
                              </strong>
                            </td>
                            <td>
                              <span className="text-success fw-bold">
                                S/ {Number(status.monto_total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td>
                              <span className="text-primary fw-bold">
                                S/ {Number(status.total_cargo_fijo).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {status.cantidad_ventas} ventas
                              </span>
                            </td>
                          </tr>
                        ))}
                        {statusData.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center text-muted">
                              No hay datos disponibles para el período seleccionado
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {statusData.length > 0 && (
                        <tfoot>
                          <tr className="table-info">
                            <td>
                              <strong>TOTAL</strong>
                            </td>
                            <td>
                              <strong>
                                S/ {statusData.reduce((sum, s) => sum + Number(s.monto_total), 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                S/ {statusData.reduce((sum, s) => sum + Number(s.total_cargo_fijo), 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                {statusData.reduce((sum, s) => sum + Number(s.cantidad_ventas), 0)} ventas
                              </strong>
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gráfico de línea mensual (común a todas las pestañas) */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                Evolución Diaria - {months[selectedMonth - 1]?.label} {selectedYear}
              </h5>
            </div>
            <div className="card-body">
              <div style={{ height: '300px' }}>
                <Line data={lineChartData} options={lineOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCharts;